import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Heart,
  Plus,
  Search,
  Filter,
  Target,
  CheckCircle2,
  Clock,
  AlertCircle,
  ChevronRight,
  MoreHorizontal,
  FileEdit,
  Trash2,
  User,
} from "lucide-react";
import { ProviderSidebar } from "@/components/layout/ProviderSidebar";
import { ProviderHeader } from "@/components/layout/ProviderHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { CarePlanDialog } from "@/components/care-plans/CarePlanDialog";
import { CarePlanDetailSheet } from "@/components/care-plans/CarePlanDetailSheet";

type CarePlanStatus = "draft" | "active" | "completed" | "discontinued";

interface CarePlan {
  id: string;
  member_id: string;
  provider_id: string;
  title: string;
  description: string | null;
  status: CarePlanStatus;
  start_date: string;
  target_end_date: string | null;
  actual_end_date: string | null;
  diagnosis: string | null;
  created_at: string;
  updated_at: string;
  member_name?: string;
  goals_count?: number;
  goals_completed?: number;
}

interface CarePlanGoal {
  id: string;
  care_plan_id: string;
  title: string;
  description: string | null;
  target_value: number | null;
  current_value: number | null;
  unit: string | null;
  priority: "low" | "medium" | "high";
  status: "not_started" | "in_progress" | "achieved" | "discontinued";
  target_date: string | null;
  created_at: string;
}

const statusConfig = {
  draft: { label: "Draft", color: "bg-muted text-muted-foreground", icon: FileEdit },
  active: { label: "Active", color: "bg-success/10 text-success", icon: CheckCircle2 },
  completed: { label: "Completed", color: "bg-primary/10 text-primary", icon: Target },
  discontinued: { label: "Discontinued", color: "bg-destructive/10 text-destructive", icon: AlertCircle },
};

export default function CarePlans() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<CarePlan | null>(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);

  // Fetch care plans with member names
  const { data: carePlans, isLoading } = useQuery({
    queryKey: ["care-plans"],
    queryFn: async () => {
      if (!user?.id) return [];
      // Get assigned member IDs
      const { data: assignments } = await supabase
        .from("provider_members" as any)
        .select("member_id")
        .eq("provider_id", user.id);
      const assignedMemberIds = (assignments || []).map((a: any) => a.member_id);
      if (assignedMemberIds.length === 0) return [];

      const { data: plans, error } = await supabase
        .from("care_plans")
        .select("*")
        .in("member_id", assignedMemberIds)
        .order("updated_at", { ascending: false });

      if (error) throw error;

      // Fetch member names from intake_forms
      const memberIds = [...new Set(plans?.map(p => p.member_id) || [])];
      const { data: intakeForms } = await supabase
        .from("patients")
        .select("user_id, first_name, last_name")
        .in("user_id", memberIds);

      // Fetch goals count for each plan
      const { data: goals } = await supabase
        .from("care_plan_goals")
        .select("care_plan_id, status");

      const memberMap = new Map(
        intakeForms?.map(f => [f.user_id, `${f.first_name} ${f.last_name}`]) || []
      );

      return plans?.map(plan => {
        const planGoals = goals?.filter(g => g.care_plan_id === plan.id) || [];
        return {
          ...plan,
          member_name: memberMap.get(plan.member_id) || "Unknown Patient",
          goals_count: planGoals.length,
          goals_completed: planGoals.filter(g => g.status === "achieved").length,
        };
      }) as CarePlan[];
    },
  });

  // Delete care plan
  const deleteMutation = useMutation({
    mutationFn: async (planId: string) => {
      const { error } = await supabase.from("care_plans").delete().eq("id", planId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["care-plans"] });
      toast({ title: "Care plan deleted successfully" });
    },
    onError: (error) => {
      toast({ title: "Error deleting care plan", description: error.message, variant: "destructive" });
    },
  });

  // Filter plans
  const filteredPlans = carePlans?.filter(plan => {
    const matchesSearch =
      plan.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      plan.member_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      plan.diagnosis?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || plan.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: carePlans?.length || 0,
    active: carePlans?.filter(p => p.status === "active").length || 0,
    completed: carePlans?.filter(p => p.status === "completed").length || 0,
    goalsAchieved: carePlans?.reduce((sum, p) => sum + (p.goals_completed || 0), 0) || 0,
  };

  const getProgress = (plan: CarePlan) => {
    if (!plan.goals_count) return 0;
    return Math.round((plan.goals_completed! / plan.goals_count) * 100);
  };

  return (
    <div className="min-h-screen bg-background">
      <ProviderSidebar />
      <div className="ml-64">
        <ProviderHeader />
        <main className="p-6">
          {/* Page Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="font-display text-3xl font-bold text-foreground">Care Plans</h1>
              <p className="mt-1 text-muted-foreground">
                Manage patient treatment plans, goals, and track progress
              </p>
            </div>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              New Care Plan
            </Button>
          </div>

          {/* Stats Overview */}
          <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Plans</p>
                    <p className="text-2xl font-bold">{stats.total}</p>
                  </div>
                  <div className="rounded-lg bg-primary/10 p-3">
                    <Heart className="h-5 w-5 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Active Plans</p>
                    <p className="text-2xl font-bold text-success">{stats.active}</p>
                  </div>
                  <div className="rounded-lg bg-success/10 p-3">
                    <CheckCircle2 className="h-5 w-5 text-success" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Completed</p>
                    <p className="text-2xl font-bold">{stats.completed}</p>
                  </div>
                  <div className="rounded-lg bg-primary/10 p-3">
                    <Target className="h-5 w-5 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Goals Achieved</p>
                    <p className="text-2xl font-bold text-success">{stats.goalsAchieved}</p>
                  </div>
                  <div className="rounded-lg bg-success/10 p-3">
                    <Target className="h-5 w-5 text-success" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search care plans..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="discontinued">Discontinued</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Care Plans List */}
          {isLoading ? (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {[1, 2, 3, 4].map(i => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <Skeleton className="h-6 w-48 mb-2" />
                    <Skeleton className="h-4 w-32 mb-4" />
                    <Skeleton className="h-2 w-full mb-2" />
                    <Skeleton className="h-4 w-24" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredPlans?.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Heart className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No care plans found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchQuery || statusFilter !== "all"
                    ? "Try adjusting your filters"
                    : "Create your first care plan to get started"}
                </p>
                <Button onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Care Plan
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {filteredPlans?.map(plan => {
                const StatusIcon = statusConfig[plan.status].icon;
                const progress = getProgress(plan);

                return (
                  <Card
                    key={plan.id}
                    className="cursor-pointer transition-all hover:shadow-lg"
                    onClick={() => {
                      setSelectedPlan(plan);
                      setDetailSheetOpen(true);
                    }}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback className="bg-primary/10 text-primary">
                              {plan.member_name?.split(" ").map(n => n[0]).join("") || "?"}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <h3 className="font-semibold">{plan.title}</h3>
                            <p className="text-sm text-muted-foreground">{plan.member_name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={statusConfig[plan.status].color}>
                            <StatusIcon className="mr-1 h-3 w-3" />
                            {statusConfig[plan.status].label}
                          </Badge>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={e => {
                                e.stopPropagation();
                                setSelectedPlan(plan);
                                setDetailSheetOpen(true);
                              }}>
                                <ChevronRight className="mr-2 h-4 w-4" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={e => {
                                  e.stopPropagation();
                                  if (confirm("Are you sure you want to delete this care plan?")) {
                                    deleteMutation.mutate(plan.id);
                                  }
                                }}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>

                      {plan.diagnosis && (
                        <p className="text-sm text-muted-foreground mb-3">
                          <span className="font-medium">Diagnosis:</span> {plan.diagnosis}
                        </p>
                      )}

                      {/* Progress */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Goals Progress</span>
                          <span className="font-medium">
                            {plan.goals_completed}/{plan.goals_count} completed
                          </span>
                        </div>
                        <Progress value={progress} className="h-2" />
                      </div>

                      {/* Dates */}
                      <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Started {format(new Date(plan.start_date), "MMM d, yyyy")}
                        </div>
                        {plan.target_end_date && (
                          <div className="flex items-center gap-1">
                            <Target className="h-3 w-3" />
                            Target {format(new Date(plan.target_end_date), "MMM d, yyyy")}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </main>
      </div>

      {/* Create Care Plan Dialog */}
      <CarePlanDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ["care-plans"] })}
      />

      {/* Care Plan Detail Sheet */}
      {selectedPlan && (
        <CarePlanDetailSheet
          open={detailSheetOpen}
          onOpenChange={setDetailSheetOpen}
          carePlan={selectedPlan}
          onUpdate={() => queryClient.invalidateQueries({ queryKey: ["care-plans"] })}
        />
      )}
    </div>
  );
}
